'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import { useEffect, useRef } from 'react';

import { familyMemberByName } from '../data';
import { ChatMessage, clockLabel, GameEvent } from '../engine';

import { FamilyAvatar } from './FamilyAvatar';

type Props = {
  chatLog: ChatMessage[];
  onRespond: (responseIndex: number) => void;
  pendingEvent?: GameEvent;
};

const isSystemSender = (from: string) => from === 'Home' || from === 'Grid Alert';

const Bubble = ({ message }: { message: ChatMessage }) => {
  if (message.from === '') {
    return (
      <x.p color="uiHelperCopy" fontStyle="italic" textAlign="center">
        {message.text}
      </x.p>
    );
  }
  if (isSystemSender(message.from)) {
    return (
      <x.div bg="uiBgAlternate" borderRadius={8} p={3} textAlign="center">
        <DS.Text text="body14">{message.text}</DS.Text>
      </x.div>
    );
  }

  const mine = message.from === 'You';
  const member = familyMemberByName(message.from);
  return (
    <x.div
      alignSelf={mine ? 'flex-end' : 'flex-start'}
      bg={mine ? 'uiHeadingCopy' : 'uiBgAlternate'}
      borderRadius={12}
      color={mine ? 'uiBg' : 'uiBodyCopy'}
      maxW="85%"
      px={4}
      py={2}
    >
      {!mine && (
        <x.span alignItems="center" display="flex" gap={1}>
          {member && <FamilyAvatar memberId={member.id} sizePx={20} />}
          <DS.Text color="uiHelperCopy" fontWeight="bold" text="body12">
            {`${message.from} · ${clockLabel(message.atMinute)}`}
          </DS.Text>
        </x.span>
      )}
      <x.p m={0}>{message.text}</x.p>
    </x.div>
  );
};

export const GroupChat = ({ chatLog, onRespond, pendingEvent }: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ behavior: 'smooth', top: scrollRef.current.scrollHeight });
  }, [chatLog.length, pendingEvent]);

  return (
    <DS.Flex border="1px solid" borderColor="uiDivider" borderRadius={12} gap={3} h="100%" minH={0} p={3}>
      <DS.Text as="h2" text="h7" textAlign="center">
        the fam
      </DS.Text>
      <x.div display="flex" flex="1" flexDirection="column" gap={3} minH={0} overflowY="auto" ref={scrollRef}>
        {chatLog.map((message) => (
          <Bubble key={`${message.atMinute}-${message.from}-${message.text}`} message={message} />
        ))}
      </x.div>
      {/* stable footprint for the reply buttons so the chat doesn't jump as prompts come and go */}
      <x.div minH="96px">
        {pendingEvent && (
          <DS.Flex gap={2}>
            {pendingEvent.responses.map(({ label }, index) => (
              <DS.Button
                fullWidth
                key={label}
                onClick={() => onRespond(index)}
                variant={index === 0 ? 'primary' : 'secondary'}
              >
                {label}
              </DS.Button>
            ))}
          </DS.Flex>
        )}
      </x.div>
    </DS.Flex>
  );
};
